/**
 * Indicator Studio: design indicators from catalogue measures, test them on
 * real data, take them through review and publish their values.
 */
import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, InputAdornment, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Indicator, errorMessage, indicatorRegistryApi } from '../../../services/indicatorRegistryApi';
import IndicatorDesigner from './IndicatorDesigner';
import IndicatorDetail from './IndicatorDetail';
import StatusChip from './StatusChip';

type View =
  | { mode: 'list' }
  | { mode: 'design'; indicator?: Indicator }
  | { mode: 'detail'; id: number };

const IndicatorStudio: React.FC = () => {
  const [view, setView] = useState<View>({ mode: 'list' });
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');

  const catalogue = useQuery(['indicator-registry', 'catalogue'], indicatorRegistryApi.catalogue);
  const list = useQuery(
    ['indicator-registry', 'list', status, search],
    () => indicatorRegistryApi.list({ latest: !status, status: status || undefined, search: search || undefined }),
    { keepPreviousData: true, enabled: view.mode === 'list' },
  );

  if (catalogue.isLoading) return <CircularProgress />;
  if (catalogue.error || !catalogue.data) return <Alert severity="error">{errorMessage(catalogue.error)}</Alert>;

  if (view.mode === 'design') {
    return (
      <IndicatorDesigner
        catalogue={catalogue.data}
        initial={view.indicator}
        onDone={saved => setView(saved ? { mode: 'detail', id: saved.id } : view.indicator
          ? { mode: 'detail', id: view.indicator.id } : { mode: 'list' })}
      />
    );
  }

  if (view.mode === 'detail') {
    return (
      <IndicatorDetail
        key={view.id}
        catalogue={catalogue.data}
        id={view.id}
        onBack={() => setView({ mode: 'list' })}
        onEdit={indicator => setView({ mode: 'design', indicator })}
        onOpen={id => setView({ mode: 'detail', id })}
      />
    );
  }

  const indicators = list.data || [];
  const unitLabel = (code: string) => catalogue.data.units.find(u => u.code === code)?.label || code;
  const categoryLabel = (code: string) => catalogue.data.categories.find(c => c.code === code)?.label || code;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6">Indicator Studio</Typography>
          <Typography variant="body2" color="text.secondary">
            Build indicators from SIS measures without SQL, test them on live data, and publish approved values.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setView({ mode: 'design' })}>
          New indicator
        </Button>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small" placeholder="Search code or name" value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <ToggleButtonGroup size="small" exclusive value={status} onChange={(_, v) => setStatus(v ?? '')}>
          <ToggleButton value="">Latest</ToggleButton>
          <ToggleButton value="draft">Drafts</ToggleButton>
          <ToggleButton value="in_review">In review</ToggleButton>
          <ToggleButton value="approved">Approved</ToggleButton>
          <ToggleButton value="retired">Retired</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {list.isError && <Alert severity="error">{errorMessage(list.error)}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell align="right">Version</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {indicators.map(ind => (
              <TableRow key={ind.id} hover sx={{ cursor: 'pointer' }} onClick={() => setView({ mode: 'detail', id: ind.id })}>
                <TableCell sx={{ fontFamily: 'monospace' }}>{ind.code}</TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{ind.name}</TableCell>
                <TableCell>{categoryLabel(ind.category)}</TableCell>
                <TableCell>{unitLabel(ind.unit)}</TableCell>
                <TableCell align="right">{ind.version}</TableCell>
                <TableCell><StatusChip status={ind.status} /></TableCell>
                <TableCell>{new Date(ind.updated_at).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
            {!list.isLoading && !indicators.length && (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  No indicators here yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default IndicatorStudio;
